import { type ReactElement, useRef, useState } from 'react';

import { RiArrowDownSLine } from '@remixicon/react';

import { Chip, EncoreProvider, Focusable } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { FilterEditorPopover } from './FilterEditorPopover';
import { useFilterPanelContext } from './FilterPanelContext';
import { HostedFilterEditorContext } from './HostedFilterEditorContext';
import { isEmptyFilterValue } from './isEmptyFilterValue';
import {
  type FilterDefinition,
  useFilters,
  useOptionalFilterEditor,
} from './provider';

import styles from './InlineFilterTrigger.module.css';

export interface InlineFilterTriggerProps {
  filter: FilterDefinition;
}

export const InlineFilterTrigger = ({
  filter,
}: InlineFilterTriggerProps): ReactElement => {
  const { activeFilters, addFilter, removeFilter } = useFilters();
  const { pinOpen: keepPanelOpen } = useFilterPanelContext();
  const removePanelFilter = (id: string): void => {
    keepPanelOpen();
    removeFilter(id);
  };
  const editorContext = useOptionalFilterEditor();
  const [editingNewFilter, setEditingNewFilter] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const open = editorContext?.activeEditorFilterId === filter.id;
  const active = activeFilters.find(f => f.id === filter.id);
  const isToggle = filter.interaction === 'toggle';
  const editedValueRef = useRef<unknown>(null);

  const setOpen = (nextOpen: boolean): void => {
    editorContext?.setActiveEditorFilterId(nextOpen ? filter.id : null);
  };

  const closePopover = (): void => {
    const value = editedValueRef.current;
    if (isEmptyFilterValue(value)) removePanelFilter(filter.id);
    setOpen(false);
    setEditingNewFilter(false);
    queueMicrotask(() => anchorRef.current?.querySelector('button')?.focus());
  };

  return (
    <span ref={anchorRef} className={styles.anchor}>
      {active && !(editingNewFilter && open) ? (
        active.renderViewer({
          onValueChange: value => addFilter(active, value),
          onRemove: () => removePanelFilter(active.id),
        })
      ) : (
        <FilterEditorPopover
          label={filter.label}
          isOpen={open}
          onOpenChange={nextOpen => (nextOpen ? setOpen(true) : closePopover())}
          trigger={triggerRef => {
            if (active)
              return (
                <HostedFilterEditorContext.Provider
                  value={{
                    filterId: filter.id,
                    isOpen: open,
                    onOpenChange: nextOpen =>
                      nextOpen ? setOpen(true) : closePopover(),
                    triggerRef,
                  }}
                >
                  {active.renderViewer({
                    onValueChange: value => addFilter(active, value),
                    onRemove: () => removePanelFilter(active.id),
                  })}
                </HostedFilterEditorContext.Provider>
              );
            const chip = (
              <Chip
                ref={triggerRef}
                size="small"
                variant="tinted"
                type="button"
                aria-expanded={isToggle ? undefined : open}
                aria-haspopup={isToggle ? undefined : 'dialog'}
                aria-pressed={isToggle ? false : undefined}
                onClick={() => {
                  keepPanelOpen();
                  setEditingNewFilter(true);
                  editedValueRef.current = null;
                  setOpen(true);
                }}
               data-track-source-anchor="49d3064ad5;inline-filter-trigger-chip">
                <span className={styles.label}>
                  {filter.label}
                  {!isToggle && <RiArrowDownSLine size={16} aria-hidden />}
                </span>
              </Chip>
            );
            return (
              <EncoreProvider config={{ next: ['chip'] }}>
                {filter.description ? (
                  <TooltipRoot>
                    <Focusable>{chip}</Focusable>
                    <Tooltip>{filter.description}</Tooltip>
                  </TooltipRoot>
                ) : (
                  chip
                )}
              </EncoreProvider>
            );
          }}
        >
          {filter.renderEditor({
            value: active?.value ?? null,
            onValueChange: value => {
              editedValueRef.current = value;
              addFilter(filter, value);
            },
            onClose: closePopover,
          })}
        </FilterEditorPopover>
      )}
    </span>
  );
};
